import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LohiDashboardComponent } from './dashboard-2025/lohi-dashboard.component';
import { AuthGuard } from './root/auth.guard';
import { BillingComponent } from './root/dashboard/billing/billing.component';
import { CarrierScorecardComponent } from './root/dashboard/carrier-scorecard/carrier-scorecard.component';
import { CoachingComponent } from './root/dashboard/coaching/coaching.component';
import { DriverPerformanceComponent } from './root/dashboard/kpi/driver-performance/driver-performance.component';
import { NavBarPerformanceComponent } from './root/dashboard/kpi/nav-bar-performance/nav-bar-performance.component';
import { RevenuePerformanceComponent } from './root/dashboard/kpi/revenue-performance/revenue-performance.component';
import { LeaderboardsComponent } from './root/dashboard/leaderboards/leaderboards.component';
import { OperationsComponent } from './root/dashboard/operations/operations.component';
import { InternalGuard } from './root/internal.guard';
import { ShiftBookingComponent } from './root/shift-booking/shift-booking.component';
import { YmtGuard } from './root/ymt.guard';

const routes: Routes = [
  {
    loadChildren: () => import('src/app/final-count/final-count.module').then((m) => m.FinalCountModule),
    path: 'final_count',
  },
  {
    loadChildren: () => import('src/app/dashboard-2025/lohi-dashboard.module').then((m) => m.Dashboard2025Module),
    path: 'v2',
    canActivate: [AuthGuard],
    data: { allowDriver: true },
  },
  {
    loadChildren: () => import('src/app/lohi-pay/lohi-pay.module').then((m) => m.LohiPayModule),
    path: 'pay',
    canActivate: [AuthGuard],
    data: { allowDriver: true },
  },
  {
    loadChildren: () =>
      import('src/app/driver-settlement/driver-settlement.module').then((m) => m.DriverSettlementModule),
    path: 'driver_settlement',
    canActivate: [AuthGuard],
    data: { allowDriver: true },
  },
  {
    loadChildren: () => import('src/app/bonuses/bonuses.module').then((m) => m.BonusesModule),
    path: 'bonuses',
    canActivate: [AuthGuard],
    data: { allowDriver: true },
  },
  {
    loadChildren: () => import('src/app/fuelcard/fuelcard.module').then((m) => m.FuelcardModule),
    path: 'fuelcard',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/manage-fleet/manage-fleet.module').then((m) => m.ManageFleetModule),
    path: 'manage-fleet',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/settlement-statement/settlement-statement.module').then((m) => m.SettlementStatementModule),
    path: 'settlement',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/notifications/notifications.module').then((m) => m.NotificationsModule),
    path: 'notifications',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/schedule-blocking/schedule-blocking.module').then((m) => m.ScheduleBlockingModule),
    path: 'scheduling',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/leaderboard/leaderboard.module').then((m) => m.LeaderboardModule),
    path: 'leaderboard_management',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/eld/eld.module').then((m) => m.EldModule),
    path: 'eld_management',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/map/map.module').then((m) => m.MapModule),
    path: 'map',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/auth/auth.module').then((m) => m.AuthModule),
    path: 'auth',
  },
  {
    loadChildren: () => import('src/app/drivers/drivers.module').then((m) => m.DriversModule),
    path: 'drivers',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/loads/loads.module').then((m) => m.LoadsModule),
    path: 'loads',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/internal/internal.module').then((m) => m.InternalModule),
    path: 'internal',
    canActivate: [AuthGuard, InternalGuard],
  },
  {
    loadChildren: () => import('src/app/needs-recovery/needs-recovery.module').then((m) => m.NeedsRecoveryModule),
    path: 'needs-recovery',
    canActivate: [AuthGuard, InternalGuard],
  },
  {
    loadChildren: () =>
      import('src/app/driver-cut-management/driver-cut-management.module').then((m) => m.DriverCutManagementModule),
    path: 'driver-cuts',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/owner-dashboard/owner-dashboard.module').then((m) => m.OwnerDashboardModule),
    path: 'owner-dashboard',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/classic-carrier-dashboard/classic-carrier-dashboard.module').then(
        (m) => m.ClassicCarrierDashboardModule,
      ),
    path: 'cc-dashboard',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/preferred-partner/preferred-partner.module').then((m) => m.PreferredPartnerModule),
    path: 'preferred-partner',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/sign-on-bonus/sign-on-bonus.module').then((m) => m.SignOnBonusModule),
    path: 'sign-on-bonus',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/tours-billing/tours-billing.module').then((m) => m.ToursBillingModule),
    path: 'tour_billing',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/asset-dashboard/asset-dashboard.module').then((m) => m.AssetDashboardModule),
    path: 'asset-dashboard',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/contracts/contracts.module').then((m) => m.ContractsModule),
    path: 'contracts',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/assets/asset.module').then((m) => m.AssetModule),
    path: 'assets-new',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/asset-maintainer-email/asset-maintainer-email.module').then((m) => m.AssetMaintainerEmailModule),
    path: 'asset-maintainer-email',
    canActivate: [AuthGuard, InternalGuard],
  },
  {
    loadChildren: () => import('src/app/sites/site.module').then((m) => m.SiteModule),
    path: 'sites',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/superhighway-driver-intake/superhighway-driver-intake.module').then(
        (m) => m.SuperhighwayDriverIntakeModule,
      ),
    path: 'superhighway',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/tours/tours.module').then((m) => m.ToursModule),
    path: 'tours',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/fivef-ratecon/fivef-ratecon.module').then((m) => m.FivefRateconModule),
    path: 'fivef-ratecon',
    canActivate: [AuthGuard],
  },
  {
    path: 'kpis',
    // component: KpiComponent,
    component: NavBarPerformanceComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'revenue-performance',
        component: RevenuePerformanceComponent,
      },
      {
        path: 'driver-performance',
        component: DriverPerformanceComponent,
      },
      {
        redirectTo: 'revenue-performance',
        path: '',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'lohi-dashboard',
    component: LohiDashboardComponent, // Use the component directly
    canActivate: [AuthGuard], // Optional: Add guards if required
    data: { allowDriver: true },
  },
  {
    path: 'coaching',
    component: CoachingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'leaderboard',
    component: LeaderboardsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'billing',
    component: BillingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'billing-export',
    component: BillingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'carrier-scorecard',
    component: CarrierScorecardComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'booking',
    component: ShiftBookingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'dashboard',
    component: OperationsComponent,
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () => import('src/app/ymt/ymt.module').then((m) => m.YmtModule),
    path: 'ymt',
    canActivate: [AuthGuard, YmtGuard],
  },
  {
    loadChildren: () => import('src/app/auto-pickups/auto-pickups.module').then((m) => m.AutoPickupsModule),
    path: 'auto-pickups',
    canActivate: [AuthGuard, InternalGuard],
  },
  {
    loadChildren: () => import('src/app/ymt-assets/ymt-assets.module').then((m) => m.YmtAssetsModule),
    path: 'asset_details',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/customer-forecast/customer-forecast.module').then((m) => m.CustomerForecastModule),
    path: 'forecast',
    canActivate: [AuthGuard],
  },
  {
    loadChildren: () =>
      import('src/app/fivef-tour-public-url/fivef-tour-public-url.module').then((m) => m.FiveFTourPublicUrlModule),
    path: 'driver_load',
  },
  // {
  //   loadChildren: () => import('src/app/holiday-2021/holiday-2021.module').then((m) => m.Holiday2021Module),
  //   path: 'h2021',
  //   canActivate: [AuthGuard],
  //   data: { allowDriver: true },
  // },
  {
    loadChildren: () => import('src/app/holiday-2024/holiday-2024.module').then((m) => m.Holiday2024Module),
    path: 'h2024',
    canActivate: [AuthGuard],
    data: { allowDriver: true },
  },
  {
    loadChildren: () => import('src/app/holiday-2023/holiday-2023.module').then((m) => m.Holiday2023Module),
    path: 'h2023',
    canActivate: [AuthGuard],
    data: { allowDriver: true },
  },
  {
    path: 'b2021',
    redirectTo: 'auth/b2021',
  },
  {
    path: '**',
    redirectTo: 'auth',
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'auth',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
